import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AdminMainContainer from '../../../../layouts/admin/AdminMainContainer'
import useAdminMedicalReimbursementDetailViewModel from './viewModel'
import { Fragment } from 'react/jsx-runtime'
import ApprovalStatusEnumHelper from '../../../../database/enums/helpers/approval-status.helper'
import { ApprovalStatus } from '../../../../database/enums/ApprovalStatus.enum'
import ReimbursementTypeEnumHelper from '../../../../database/enums/helpers/reimbursement-type.helper'
import CurrencyEnumHelper from '../../../../database/enums/helpers/currency.helper'
import MedicalReimbursementService from '../../../../services/medical-reimbursement'
import ApprovalPopup from '../components/ApprovalPopup'

export default function AdminMedicalReimbursementDetailPage() {
  const viewModel = useAdminMedicalReimbursementDetailViewModel()

  const renderLoadingState = () => {
    return (
      <Box sx={{ width: '100%', maxWidth: '500px' }}>
        <Card elevation={5}>
          <CardHeader
            title={<Skeleton variant='text' width={260} />}
            subheader={<Skeleton variant='text' width={100} />}
            action={<Skeleton variant='rounded' width={80} />}
          />
          <Divider />
          <CardContent>
            <Skeleton variant='text' width={200} />
            <Skeleton variant='text' width={240} />
            <Skeleton variant='text' width={260} />
          </CardContent>
          <Divider />
          <CardContent>
            <Skeleton variant='rounded' width={'100%'} height={60} />
          </CardContent>
          <Divider />
          <CardContent>
            <Skeleton variant='rounded' width={'100%'} height={30} />
          </CardContent>
        </Card>
      </Box>
    )
  }

  const renderDetailState = () => {
    if (!!viewModel.detailData)
      return (
        <Box sx={{ width: '100%', maxWidth: '500px' }}>
          <Card elevation={5}>
            <CardHeader
              title={'Medical Reimbursement'}
              subheader={ReimbursementTypeEnumHelper.getLabel(viewModel.detailData.reimbursementType)}
              action={
                <Chip
                  label={ApprovalStatusEnumHelper.getLabel(viewModel.detailData.approvalStatus)}
                  color={ApprovalStatusEnumHelper.getColor(viewModel.detailData.approvalStatus)}
                />
              }
            />
            <Divider />
            <CardContent>
              <Typography fontSize={22} fontWeight={500}>
                {viewModel.detailData.name}
              </Typography>
              <Typography>
                {'on '}
                <Typography component={'span'} fontWeight={600}>
                  {viewModel.detailData.vendor}
                </Typography>
              </Typography>
              <Chip
                variant='outlined'
                label={`Date: ${viewModel.getDateValue(viewModel.detailData.serviceDate)}`}
                sx={{ mt: 1 }}
              />
              <Typography fontWeight={500} fontSize={32} sx={{ mt: 1 }}>
                <Typography component='span' fontWeight={'inherit'} fontSize={'inherit'}>
                  {CurrencyEnumHelper.getSymbol(viewModel.detailData.totalAmountClaimed.currency)}
                </Typography>
                <Typography component='span' fontWeight={'inherit'} fontSize={'inherit'}>
                  {Intl.NumberFormat().format(viewModel.detailData.totalAmountClaimed.value)}
                </Typography>
              </Typography>
            </CardContent>
            <Divider />
            <CardContent>
              <TextField
                value={!!viewModel.detailData.description ? viewModel.detailData.description : '-'}
                fullWidth
                variant='outlined'
                label='Description'
                multiline
              />
            </CardContent>
            {!!viewModel.detailData.supportingDocumentRef && !!viewModel.detailData.supportingDocumentUrl ? (
              <Fragment>
                <Divider />
                <CardContent>
                  <a
                    href={viewModel.detailData.supportingDocumentUrl}
                    download={MedicalReimbursementService.getFileNameFromRef(
                      viewModel.detailData.supportingDocumentRef,
                    )}
                    target='_blank'
                    rel='noreferrer'
                  >
                    <Button variant='contained' fullWidth>
                      Download file attachment
                    </Button>
                  </a>
                </CardContent>
              </Fragment>
            ) : null}
          </Card>
          {viewModel.detailData.approvalStatus === ApprovalStatus.UNRESPONDED ? (
            <Stack mt={2} direction={'row'} alignItems={'center'} justifyContent={'end'} gap={2}>
              <Button
                variant='contained'
                color={viewModel.canApproval ? 'success' : 'inherit'}
                onClick={viewModel.onApproveClick}
              >
                Approve
              </Button>
              <Button
                variant='contained'
                color={viewModel.canApproval ? 'error' : 'inherit'}
                onClick={viewModel.onRejectClick}
              >
                Reject
              </Button>
            </Stack>
          ) : null}
        </Box>
      )
    // ! NOTES: This will never happen
    return null
  }

  const renderErrorState = () => {
    return (
      <Alert severity='error' variant='filled'>
        <AlertTitle>Error!</AlertTitle>
        Document not found!
      </Alert>
    )
  }

  return (
    <AdminMainContainer title='Medical Reimbursement Detail' backButton={{ show: true }}>
      {viewModel.dataFetchStatus.isLoading
        ? renderLoadingState()
        : viewModel.dataFetchStatus.isSuccess && !!viewModel.detailData
          ? renderDetailState()
          : viewModel.dataFetchStatus.isError
            ? renderErrorState()
            : null}

      <ApprovalPopup onConfirm={viewModel.onApprovalPopupConfirm} />
    </AdminMainContainer>
  )
}
